import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTimeoutFn } from 'react-use';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MISSIONS_CATEGORY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { MissionToastBackground } from '../MissionToastBackground';
import type { CollectionToastContent_taskInterface } from './__generated__/index.graphql';

const Icon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;
const SecondLine = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  gap: var(--intermediate-unit);
  align-items: center;
`;

type Props = {
  task: CollectionToastContent_taskInterface;
  previousProgress: number;
  onClose: () => void;
};
export const CollectionToastContent = ({
  task,
  previousProgress,
  onClose,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const [displayedProgress, setDisplayedProgress] = useState(previousProgress);

  const isCardsCountTask = isType(task, 'CardsCountTask');
  const isCompleteCollectionsCountTask = isType(
    task,
    'CompleteCollectionsCountTask'
  );
  const { title, iconUrl, progress, target, color } = task;

  useTimeoutFn(() => {
    setDisplayedProgress(progress);
  }, 200);

  if (!isCardsCountTask && !isCompleteCollectionsCountTask) {
    return null;
  }

  return (
    <Link
      to={`${generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
        params: {
          rarity: Rarity.common.toLowerCase(),
          category: 'collect',
        },
      })}`}
      onClick={onClose}
    >
      <MissionToastBackground color={color}>
        <Vertical>
          <Horizontal>
            <HeadlineM>{title}</HeadlineM>
            {!!iconUrl && <Icon src={iconUrl} alt="" />}
          </Horizontal>
          <SecondLine>
            <LabelM bold>
              {isCardsCountTask ? (
                <FormattedMessage {...fantasy.collectionMilestones} />
              ) : (
                <FormattedMessage {...fantasy.completionMilestones} />
              )}
            </LabelM>
            <Progress
              skipStartAnimation
              value={displayedProgress}
              max={target}
              color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
            />
            <Horizontal gap={0}>
              <LabelM bold>{displayedProgress}</LabelM>
              <LabelM color="var(--c-nd-600)">&nbsp;/&nbsp;{target}</LabelM>
            </Horizontal>
          </SecondLine>
        </Vertical>
      </MissionToastBackground>
    </Link>
  );
};

CollectionToastContent.fragments = {
  taskInterface: gql`
    fragment CollectionToastContent_taskInterface on TaskInterface {
      id
      title
      iconUrl
      progress
      target
      color
    }
  ` as TypedDocumentNode<CollectionToastContent_taskInterface>,
};
